import React from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import type { RetainedBeaconChoice, RetainedBeaconEligibility } from '@/hooks/useRetainedBeaconDischarge';
import { useScope } from '@/hooks/useScope';

export const RETAINED_BEACON_DISCHARGE_COPY = {
  title: 'Confirm Discharge',
  prompt:
    'Will the patient remain in the hospital until the next procedure? If YES, do not remove the Ospitek bracelet.',
  keepBracelet: 'YES Discharge & Keep Bracelet',
  removeBracelet: 'NO Discharge & Remove Bracelet',
  cancel: 'Cancel',
  conflictError:
    'The patient is no longer eligible to keep this bracelet. Review the schedule, then choose NO Discharge & Remove Bracelet or cancel.',
} as const;

interface Props {
  eligibility?: RetainedBeaconEligibility | null;
}

interface ActionProps {
  onChoose: (choice: RetainedBeaconChoice) => void | Promise<void>;
  onCancel: () => void;
  working?: boolean;
}

export function formatRetainedScheduledStart(value?: string | null, timeZone?: string) {
  if (!value) return null;
  const date = new Date(value);
  const datePart = new Intl.DateTimeFormat('en-US', {
    timeZone,
    dateStyle: 'short',
  }).format(date);
  const timePart = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeStyle: 'short',
  }).format(date);
  return `${datePart} at ${timePart}`;
}

export function formatNextProcedureLine(value?: string | null, timeZone?: string) {
  const scheduledStart = formatRetainedScheduledStart(value, timeZone);
  return scheduledStart ? `Next procedure: ${scheduledStart}` : null;
}

export function RetainedBeaconDischargeChoice({ eligibility }: Props) {
  const scope = useScope();
  const hospitalTimeZone = scope?.hospital?.timezone?.id ?? 'UTC';
  const nextProcedure = formatNextProcedureLine(eligibility?.scheduledStart, hospitalTimeZone);

  return (
    <Box>
      {nextProcedure && (
        <Typography variant="body2" style={{ marginBottom: 12 }}>
          {nextProcedure}
        </Typography>
      )}
      <Typography>{RETAINED_BEACON_DISCHARGE_COPY.prompt}</Typography>
    </Box>
  );
}

export function RetainedBeaconDischargeActions({ onChoose, onCancel, working = false }: ActionProps) {
  return (
    <>
      <Button
        onClick={() => onChoose('retain')}
        color="primary"
        variant="contained"
        disabled={working}
        style={{ whiteSpace: 'normal', lineHeight: 1.2 }}
      >
        {RETAINED_BEACON_DISCHARGE_COPY.keepBracelet}
      </Button>
      <Button
        onClick={() => onChoose('standard')}
        color="primary"
        variant="outlined"
        disabled={working}
        style={{ borderColor: 'currentColor', whiteSpace: 'normal', lineHeight: 1.2 }}
      >
        {RETAINED_BEACON_DISCHARGE_COPY.removeBracelet}
      </Button>
      <Button onClick={onCancel} color="secondary" disabled={working}>
        {RETAINED_BEACON_DISCHARGE_COPY.cancel}
      </Button>
    </>
  );
}
